import React from 'react';
import { Alert, Box, Button, Drawer, Typography } from '@mui/material';
import { HealthIssue } from '../types';
import { COPY } from '../presentationCopy';

interface HealthPanelProps {
  open: boolean;
  issues: HealthIssue[];
  readyBlocked: boolean;
  aiEnabled?: boolean;
  onClose: () => void;
  onGoTo: (issue: HealthIssue) => void;
  onFixWithAi?: (issue: HealthIssue) => void;
}

const HealthPanel: React.FC<HealthPanelProps> = ({ open, issues, readyBlocked, aiEnabled, onClose, onGoTo, onFixWithAi }) => {
  const blockers = issues.filter((issue) => issue.severity === 'blocker');
  const warnings = issues.filter((issue) => issue.severity === 'warning');
  const aiSuggestions = aiEnabled
    ? issues.filter((issue) => issue.code === 'text-overflow' || issue.code === 'font-unapproved')
    : [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 380, p: 2 }}>
        <Typography variant="h6" sx={{ color: '#0A2757', mb: 1 }}>{COPY.healthTitle}</Typography>
        {readyBlocked ? (
          <Alert severity="error" sx={{ mb: 2 }}>{COPY.blocked}</Alert>
        ) : (
          <Alert severity="success" sx={{ mb: 2 }}>{COPY.noBlockers}</Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>{COPY.blockers} ({blockers.length})</Typography>
        {blockers.map((issue, index) => (
          <Box key={`${issue.code}-${index}`} sx={{ mb: 1, p: 1, border: '1px solid #f3c2c2', borderRadius: 1 }}>
            <Typography variant="body2">{issue.message}</Typography>
            <Button size="small" onClick={() => onGoTo(issue)}>{COPY.goToIssue}</Button>
          </Box>
        ))}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>{COPY.warnings} ({warnings.length})</Typography>
        {warnings.map((issue, index) => (
          <Box key={`${issue.code}-${index}`} sx={{ mb: 1, p: 1, border: '1px solid #e6d7a8', borderRadius: 1 }}>
            <Typography variant="body2">{issue.message}</Typography>
            <Button size="small" onClick={() => onGoTo(issue)}>{COPY.goToIssue}</Button>
          </Box>
        ))}

        {aiEnabled && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>{COPY.aiSuggestions} ({aiSuggestions.length})</Typography>
            {aiSuggestions.map((issue, index) => (
              <Box key={`ai-${issue.code}-${index}`} sx={{ mb: 1, p: 1, border: '1px solid #c5d3ea', borderRadius: 1 }}>
                <Typography variant="body2">{issue.message}</Typography>
                {onFixWithAi && (
                  <Button size="small" onClick={() => onFixWithAi(issue)}>{COPY.aiFix}</Button>
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default HealthPanel;
